/**
 * useRowMutations —— 合作内容行的「本地 id 归一化 + 受控写回」。
 *
 * 【为什么必须自持 `tableListValue` / `handleTableChange`，而不是留在容器】
 * 这对函数是**同一机制的两半**，且是后续所有回写的前置条件：
 *  ① `tableListValue` 给没有 id 的行补本地兜底 id（渲染 key + 校验定位都依赖 `rowKey="id"`）；
 *  ② `handleTableChange` 在写回业务数据前把本地 id 剥掉。
 * ⚠️ 所有行级/属性级回调都**必须基于 `tableListValue`** 做行匹配：表格单元格回传的 id 来自
 * 渲染行，对无真实 id 的行就是本地兜底 id；若拿原始 `value` 匹配会**全部落空**（值写不进去，
 * 不报错、只是改了没反应）。
 *
 * 如果把它们留在容器、只把回调搬进来，这条约束就变成「跨文件的隐式约定」——
 * 而这正是最容易在后续维护中被写错的地方。故整对一起搬。
 */
import { useMemo } from 'react';
import { useMemoizedFn } from 'ahooks';
import { Message } from '@bedrock/components';
import { isEmptyFieldValue } from './propertyValidation';
import {
  alignPropertyValues,
  cooperationValueToFormValue,
  formValueToCooperationValue,
} from './propertyPipe';
import { applyValuesToRows, applySubArrayToRows, type CoverMode } from './applyToWholeOrder';
import { ADD_ROW_RELEASE_TIME_DELAY_MS } from './releaseTime';

/** 本地兜底行 id 前缀：仅用于表格渲染 key 与校验定位，回写业务数据前剥离（见 handleTableChange） */
const LOCAL_ROW_ID_PREFIX = 'cooperation-local-row-';

export interface UseRowMutationsParams {
  /** 受控原始值（**不含**本地兜底 id） */
  value: Record<string, any>[];
  onChange: (next: Record<string, any>[]) => void;
  schema: any;
  /** 新增一行后重算发布时间（对齐折叠面板） */
  updateReleaseTime?: (values: any, add?: boolean) => void;
}

export interface UseRowMutationsResult {
  /** 渲染副本：缺 id 的行补本地兜底 id（表格渲染 key + 校验定位用） */
  tableListValue: Record<string, any>[];
  /** 写回入口：剥离本地兜底 id 后交给 onChange */
  handleTableChange: (next: Record<string, any>[]) => void;
  updateProperty: (id: number, code: string, v: any) => void;
  updateRow: (id: number, patch: Record<string, any>) => void;
  batchApplyProperty: (code: string, bv: any, mode: 'keep' | 'cover') => void;
  batchApplyRow: (field: string, bv: any, mode: 'keep' | 'cover') => void;
  copyRow: (id: number) => void;
  deleteRow: (id: number) => void;
  applyRowToWholeOrder: (id: number, applyConfig: { coverMode: CoverMode; coverKeys: string[] }) => void;
  /**
   * 子数组级「应用至整单」：把 `id` 行 `subKey` 子数组按 `coverMode` 写入全部合作内容行。
   * 对应卡片模式 KPITable / EquityFormTable 的同名按钮
   * （`subKey` ∈ kpi_values / necessary_rights / expect_rights）。
   *
   * `coverMode` 省略时按 `'overwrite'`（保持既有行为）；弹层调用方应显式传入。
   */
  applySubArrayToWholeOrder: (id: number, subKey: string, coverMode?: CoverMode) => void;
  addRow: () => void;
}

export function useRowMutations({
  value,
  onChange,
  schema,
  updateReleaseTime,
}: UseRowMutationsParams): UseRowMutationsResult {
  // ---- 行 id 归一化（表格渲染 key + 校验定位依赖） ----
  /**
   * 合作内容行可能没有 id（新建草稿为 `[{}]`、新增行只有本地字段），而引擎的
   * `rowKey="id"` 与校验定位（按 rowId 查 `[data-row-key]`）都依赖它：缺失时表格只能用
   * 兜底 key 渲染，表现为「校验能标红、但点提交/校验后不滚动定位」。
   * 与子项 id 兜底（kpiSubArraySpec/equitySubArraySpec 的 getItems）同一模式。
   *
   * ⚠️ 本地 id 仅用于渲染与定位，回写时由 handleTableChange 剥离，不进入业务数据。
   */
  const tableListValue = useMemo(
    () => (value || []).map((r, i) => (r?.id != null ? r : { ...r, id: `${LOCAL_ROW_ID_PREFIX}${i}` })),
    [value],
  );
  const handleTableChange = useMemoizedFn((next: Record<string, any>[]) => {
    onChange(next.map((r) => {
      if (typeof r?.id === 'string' && r.id.startsWith(LOCAL_ROW_ID_PREFIX)) {
        const { id: _localId, ...rest } = r;
        return rest;
      }
      return r;
    }));
  });

  // ---- 行级/属性级更新（稳定回调） ----
  /**
   * ⚠️ 这些回调必须基于 `tableListValue`（含本地兜底 id 的渲染副本）做行匹配：
   * 表格单元格回传的 id 来自渲染行，对无真实 id 的行即为本地兜底 id；
   * 若拿原始 `value` 匹配会全部落空（值写不进去）。回写统一走 handleTableChange 剥离本地 id。
   */
  const updateProperty = useMemoizedFn((id: number, code: string, v: any) => {
    handleTableChange(tableListValue.map((r) => (r.id === id ? { ...r, property_values: formValueToCooperationValue({ ...cooperationValueToFormValue(r.property_values), [code]: v }, schema) } : r)));
  });
  // 行级字段更新（remark / need_ai 等，非 property）
  const updateRow = useMemoizedFn((id: number, patch: Record<string, any>) => {
    handleTableChange(tableListValue.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  });
  // 批量编辑：属性列按 property_code 写回所有行（mode keep=空才填 / cover=覆盖，对齐原版 getCoverByCoverMode 语义）
  const batchApplyProperty = useMemoizedFn((code: string, bv: any, mode: 'keep' | 'cover') => {
    handleTableChange(tableListValue.map((r) => {
      const formMap = cooperationValueToFormValue(r.property_values);
      const orig = formMap[code];
      const nextVal = mode === 'keep' ? (isEmptyFieldValue(orig) ? bv : orig) : bv;
      return { ...r, property_values: formValueToCooperationValue({ ...formMap, [code]: nextVal }, schema) };
    }));
  });
  // 批量编辑：行级标量字段（remark / need_ai 等）写回所有行
  const batchApplyRow = useMemoizedFn((field: string, bv: any, mode: 'keep' | 'cover') => {
    handleTableChange(tableListValue.map((r) => {
      const orig = r[field];
      return { ...r, [field]: mode === 'keep' ? (isEmptyFieldValue(orig) ? bv : orig) : bv };
    }));
  });

  const copyRow = useMemoizedFn((id: number) => {
    const idx = tableListValue.findIndex((r) => r.id === id);
    if (idx < 0) return;
    const src = tableListValue[idx];
    const copy = { ...src, id: src.id + '-c' + Date.now() };
    for (const key of ['kpi_values', 'necessary_rights', 'expect_rights']) {
      copy[key] = (src[key] || []).map((it: any) => ({ ...it, id: it.id + '-c' + Date.now() }));
    }
    const next = [...tableListValue]; next.splice(idx + 1, 0, copy); handleTableChange(next);
  });
  const deleteRow = useMemoizedFn((id: number) => handleTableChange(tableListValue.filter((r) => r.id !== id)));

  /**
   * 字段应用至整单：复用 CooperationFormList/index.tsx 原交互 —— DropdownListApplyToTheWholeOrder
   * 弹层选择「有内容时是否批量覆盖」coverMode + 「应用至整单的字段」coverKeys 后，
   * 调用共享纯函数 applyValuesToRows（./applyToWholeOrder.ts）覆盖其余行，与折叠模式行为等价。
   */
  const applyRowToWholeOrder = useMemoizedFn((id: number, applyConfig: { coverMode: CoverMode; coverKeys: string[] }) => {
    const idx = tableListValue.findIndex((r) => r.id === id);
    if (idx < 0) return;
    handleTableChange(applyValuesToRows(tableListValue, idx, applyConfig));
    Message.success('应用成功');
  });

  /**
   * 子数组级「应用至整单」：把 `id` 行 `subKey` 子数组按 `coverMode` 写入所有合作内容行。
   *
   * 覆盖语义见 `applyToWholeOrder.ts` 的 `applySubArrayToRows`：
   *  - `retain`（保留原值）：仅目标行该子数组为空才整份填入，非空则完全不动；
   *  - `overwrite`（覆盖原值）：整份覆盖（不 merge / 不去重 / 不追加；源行自身也写成同一值）。
   *
   * 【与上一个 `applyRowToWholeOrder` 的区别（名字像、语义不同，勿混用）】
   *  - `applyRowToWholeOrder`：**行级 · 按字段** —— 弹层勾选 `coverKeys` + retain/overwrite；
   *  - 本方法：**子数组级** —— 覆盖单位是整个子数组，分别由三类区块的「应用至整单」按钮触发。
   *
   * ⚠️ 必须基于 `tableListValue`（含本地兜底 id 的渲染副本）匹配行：按 id 找到源行下标后，
   * 整表写回走 `handleTableChange`（统一剥离本地 id）—— 见本文件头的不变量。
   */
  const applySubArrayToWholeOrder = useMemoizedFn((id: number, subKey: string, coverMode: CoverMode = 'overwrite') => {
    const sourceIndex = tableListValue.findIndex((r) => r.id === id);
    if (sourceIndex < 0) return;
    handleTableChange(applySubArrayToRows(tableListValue, sourceIndex, subKey, coverMode));
    // 文案按模式区分：retain 只会填「原本空着」的行，说「已覆盖填写到全部」会与事实不符
    Message.success(
      coverMode === 'retain' ? '应用成功，已填充到有空值的合作内容' : '应用成功，已覆盖填写到全部合作内容',
    );
  });

  /** 新增一行：按 schema 补全空属性集 + 空指标/权益子数组（表内追加，非子数组内追加）。 */
  const addRow = useMemoizedFn(() => {
    const newRow: Record<string, any> = {
      // ⚠️ 新行 id 必须带 LOCAL_ROW_ID_PREFIX 前缀：此前写成 `row-${Date.now()}`，
      // 逃过了 handleTableChange 的前缀剥离，以 `row-1789456249249` 形态留在业务数据里，
      // 被「导出当前数据」的 `...item` 原样写进 Excel 的 id 列，回流导入时后端
      // DemandCooperateAccountDto.id（Integer）解析该字符串失败（#46371）。
      // 本文件头声明的不变量是「本地 id 不进入业务数据」——此处即其唯一破口。
      id: `${LOCAL_ROW_ID_PREFIX}${Date.now()}`,
      property_values: alignPropertyValues([], schema),
      kpi_values: [],
      necessary_rights: [],
      expect_rights: [],
      need_ai: undefined,
      remark: '',
    };
    handleTableChange([...tableListValue, newRow]);
    // 新增合作内容后带出「执行日期」→ 该行的「发布时间」（触发点 T2，与卡片模式同一条核心逻辑）。
    // ⚠️ 必须延迟：新行要先经 handleTableChange → onChange → planInfo 落地，带出才有对象可写；
    // 延迟常量放在 ./releaseTime 供两种模式共用，避免两边各写一个数、改漏一边。
    setTimeout(() => updateReleaseTime?.({}, true), ADD_ROW_RELEASE_TIME_DELAY_MS);
  });

  return {
    tableListValue,
    handleTableChange,
    updateProperty,
    updateRow,
    batchApplyProperty,
    batchApplyRow,
    copyRow,
    deleteRow,
    applyRowToWholeOrder,
    applySubArrayToWholeOrder,
    addRow,
  };
}
